import {readSearchOrigin} from './search-origin.mjs';
import {readFilters, LEGACY_STATUS} from './filters.mjs';
import {readHits} from './hits.mjs';
export const UUID4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?![\s\S])/i;
export function sessionId(params, storage) {
  let id = params.get('session_id');
  // Legacy search links remain independent of another search previously opened in this tab.
  if (!params.has('session_id') && !['source', 'hits', 'query_lat', 'site'].some(key => params.has(key))) {
    try { id = storage?.getItem('w2c-session'); } catch { /* Blocked storage. */ }
  }
  if (!UUID4.test(id || '')) return null;
  try { storage?.setItem('w2c-session', id); } catch { /* The canonical URL is the reload fallback. */ }
  return id;
}
export function parseSearchState(state) {
  if (!state || typeof state.params !== 'string' || state.params.length > 8192) throw Error('search state');
  const params = new URLSearchParams(state.params);
  const allowed = ['hits','source','query_lat','query_lon','query_label','connectors','min_kw','max_kw','max_price','only_free','hide_out_of_service',...LEGACY_STATUS];
  if ([...params.keys()].some(key => !allowed.includes(key) || params.getAll(key).length !== 1)) throw Error('search parameters');
  const origin = readSearchOrigin(params);
  if (!origin.point || !['gps','address'].includes(origin.source)) throw Error('search origin');
  if (!Array.isArray(state.bounds) || state.bounds.length > 25 || state.bounds.some(p =>
    !Array.isArray(p) || p.length !== 2 || !p.every(Number.isFinite) || p[0] < 5.5 || p[0] > 15.6 || p[1] < 47 || p[1] > 55.2)) throw Error('search bounds');
  return {origin, filters: readFilters(params), hits: readHits(params.get('hits')), bounds: state.bounds};
}

// This stream is independent of occupancy/live preferences and its Last-Event-ID.
export function startSearchSession(id, apply, {source = url => new EventSource(url), fetcher = fetch,
  doc = document, host = globalThis, timers = globalThis, initialSeq = 0} = {}) {
  if (!UUID4.test(id || '')) return () => {};
  const acknowledgments = new Set();
  let stream, retry, watchdog, generation = 0, stopped = false, expired = false, lastSeq = Number.isSafeInteger(initialSeq) && initialSeq > 0 ? initialSeq : 0, applying = 0;
  const active = () => !stopped && !expired && !doc.hidden && host.navigator?.onLine !== false;
  function close() { for (const controller of acknowledgments) controller.abort(); acknowledgments.clear(); generation++; stream?.close(); stream = null; timers.clearTimeout(retry); timers.clearTimeout(watchdog); }
  function failed() { close(); if (active()) retry = timers.setTimeout(connect, 3000); }
  function activity() { timers.clearTimeout(watchdog); watchdog = timers.setTimeout(failed, 40000); }
  function connect() {
    close(); if (!active()) return;
    const token = generation;
    stream = source(`/api/search-session?session_id=${encodeURIComponent(id)}`);
    const touch = () => { if (token === generation) activity(); };
    stream.onopen = touch;
    stream.addEventListener('heartbeat', touch);
    stream.addEventListener('expired', () => { if (token === generation) { expired = true; close(); } });
    stream.addEventListener('search', async event => {
      if (token !== generation) return;
      activity();
      try {
        const data = JSON.parse(event.data);
        if (!Number.isSafeInteger(data.seq) || data.seq < 1 || !UUID4.test(data.connection || '') || data.seq < lastSeq) return;
        const revision = ++applying;
        // Replay on reconnect is acknowledged without resetting manual camera/filter changes.
        if (data.seq > lastSeq) {
          await apply(parseSearchState(data.state), data.seq);
          if (token !== generation || revision !== applying) return;
          lastSeq = data.seq;
        }
        const controller = new AbortController(); acknowledgments.add(controller);
        const deadline = timers.setTimeout(() => controller.abort(), 2000);
        try {
          await fetcher('/api/search-ack', {method:'POST', credentials:'same-origin', cache:'no-store', signal:controller.signal,
            headers:{'Content-Type':'application/json'}, body:JSON.stringify({session_id:id, connection:data.connection, seq:data.seq})});
        } finally { timers.clearTimeout(deadline); acknowledgments.delete(controller); }
      } catch { /* Never acknowledge a state the browser failed to apply. */ }
    });
    stream.onerror = () => { if (token === generation) failed(); };
    activity();
  }
  const visibility = () => active() ? connect() : close();
  doc.addEventListener('visibilitychange', visibility);
  host.addEventListener?.('offline', visibility); host.addEventListener?.('online', visibility);
  connect();
  return () => { stopped = true; close(); doc.removeEventListener('visibilitychange', visibility); host.removeEventListener?.('offline', visibility); host.removeEventListener?.('online', visibility); };
}
